import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ShoppingCart, Check, Baby } from 'lucide-react'
import { cn } from '@/lib/utils'
import { db, type Product, type CartItem } from '@/lib/db'

type ProductCardProps = {
  product: Product
  className?: string
}

export default function ProductCard({ product, className }: ProductCardProps) {
  const [adding, setAdding] = useState(false)
  const [added, setAdded] = useState(false)

  const priceFormatted = (product.price ?? 0).toLocaleString('vi-VN')
  const hasStock = (product.stock ?? 1) > 0

  const addToCart = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!hasStock || adding) return

    setAdding(true)
    try {
      const cartItem: CartItem = {
        id: crypto.randomUUID(),
        product_id: product.id,
        name: product.name,
        price: product.price,
        image: product.image ?? '',
        quantity: 1,
        created_at: new Date().toISOString()
      }
      const { error } = await db.insert('cart_items', cartItem)
      if (!error) {
        setAdded(true)
        setTimeout(() => setAdded(false), 1500)
      }
    } finally {
      setAdding(false)
    }
  }

  return (
    <Link
      to={`/products/${product.id}`}
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-[var(--radius)] border border-white/70 bg-white/60 backdrop-blur-sm',
        'transition-all duration-300 hover:-translate-y-1 hover:border-[var(--color-accent)]/35 hover:shadow-lg hover:shadow-cyan-900/5',
        className
      )}
    >
      {/* Ảnh sản phẩm */}
      <div data-upbo-src="src/components/ProductCard.tsx:55" className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-cyan-50 to-[var(--color-accent)]/10">
        {product.image ? (
          <img data-upbo-src="src/components/ProductCard.tsx:57"
            src={product.image}
            alt={product.name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div data-upbo-src="src/components/ProductCard.tsx:64" className="flex h-full w-full items-center justify-center">
            <Baby className="h-12 w-12 text-[var(--color-accent)]/40" />
          </div>
        )}

        {/* Badge danh mục */}
        {product.category && (
          <span data-upbo-src="src/components/ProductCard.tsx:71" className="absolute left-3 top-3 rounded-full bg-[var(--color-accent)] px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.1em] text-white shadow-sm">
            {product.category}
          </span>
        )}

        {/* Hết hàng */}
        {!hasStock && (
          <div data-upbo-src="src/components/ProductCard.tsx:78" className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-[2px]">
            <span data-upbo-src="src/components/ProductCard.tsx:79" className="rounded-full bg-[var(--color-ink)] px-4 py-1.5 text-xs font-semibold text-white">
              Hết hàng
            </span>
          </div>
        )}
      </div>

      {/* Thông tin */}
      <div data-upbo-src="src/components/ProductCard.tsx:87" className="flex flex-1 flex-col p-4">
        <h3 data-upbo-src="src/components/ProductCard.tsx:88" className="line-clamp-2 font-semibold leading-snug text-[var(--color-ink)] transition-colors group-hover:text-[var(--color-accent)]">
          {product.name}
        </h3>

        {product.short_description && (
          <p data-upbo-src="src/components/ProductCard.tsx:93" className="mt-1 line-clamp-1 text-sm text-[var(--color-muted)]">
            {product.short_description}
          </p>
        )}

        <div data-upbo-src="src/components/ProductCard.tsx:98" className="mt-auto flex items-end justify-between pt-4">
          <div data-upbo-src="src/components/ProductCard.tsx:99">
            <p data-upbo-src="src/components/ProductCard.tsx:100" className="text-xs font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]">
              Giá
            </p>
            <p data-upbo-src="src/components/ProductCard.tsx:103" className="text-lg font-bold tracking-tight text-[var(--color-ink)]">
              {priceFormatted} <span data-upbo-src="src/components/ProductCard.tsx:104" className="text-sm font-semibold text-[var(--color-muted)]">₫</span>
            </p>
          </div>

          <button data-upbo-src="src/components/ProductCard.tsx:108"
            onClick={addToCart}
            disabled={!hasStock || adding}
            aria-label={added ? 'Đã thêm vào giỏ hàng' : 'Thêm vào giỏ hàng'}
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full transition-all duration-200',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2',
              added
                ? 'bg-emerald-500 text-white shadow-sm'
                : hasStock
                  ? 'bg-[var(--color-accent)] text-white shadow-sm hover:bg-[var(--color-accent)]/85 active:scale-95'
                  : 'cursor-not-allowed bg-[var(--color-muted)]/20 text-[var(--color-muted)]'
            )}
          >
            {added ? (
              <Check className="h-4 w-4" />
            ) : (
              <ShoppingCart className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </Link>
  )
}
